import { ref, computed, toValue, type MaybeRefOrGetter } from 'vue'
import axios, { AxiosError } from 'axios'
import { z } from 'zod'

/* ──────────────────────────────
 * Zod schemas (API truth)
 * ────────────────────────────── */

const MatchingPremiseSchema = z.object({
  highlight: z.string(),
})


const ArgumentSearchHitSchema = z.object({
  id: z.string(),
  stance: z.enum(['pro', 'con']).optional(),

  claim: z.string().optional(),
  key_point: z.string().nullable().optional(),

  score: z.number(),
  quality_score: z.number().nullable().optional(),

  url: z.string().url().optional(),
  matching_premises: z.array(MatchingPremiseSchema).optional(),
  top_premise: MatchingPremiseSchema.nullable().optional(),
})
.transform((data) => ({
  ...data,
  quality_score: data.quality_score ?? 0,
  key_point: data.key_point ?? undefined,
  matching_premises: data.matching_premises ?? []
}))

const SearchResponseSchema = z.object({
  hits: z.array(ArgumentSearchHitSchema),
  total: z.number().optional(),
  took_ms: z.number().optional(),
})

/* ──────────────────────────────
 * Types inferred from Zod (now normalized)
 * ────────────────────────────── */

export type ArgumentSearchHit = z.infer<typeof ArgumentSearchHitSchema>
// Now score is: number (always defined, defaults to 0)
// And key_point is: string | undefined (no more null)

export type SearchResponse = z.infer<typeof SearchResponseSchema>
/* ──────────────────────────────
 * Error types
 * ────────────────────────────── */

type NormalizedError = {
  message: string
  code: number | string
  reason?: string | null
}

type SortField = 'relevance' | 'quality'

type UseArgumentSearchOptions = {
  claimId?: MaybeRefOrGetter<string | null | undefined>
  limit?: number
}

/* ──────────────────────────────
 * Composable
 * ────────────────────────────── */

export function useArgumentSearch(options: UseArgumentSearchOptions = {}) {
  const q = ref('')
  const stance = ref<'all' | 'pro' | 'con'>('all')
  const sortField = ref<SortField>('relevance')
  const pageSize = options.limit ?? 5

  const resp = ref<SearchResponse | null>(null)
  const proResp = ref<SearchResponse | null>(null)
  const conResp = ref<SearchResponse | null>(null)
  const err = ref<NormalizedError | null>(null)
  const loading = ref(false)

  let currentAbort: AbortController | null = null
  let seq = 0

  const API_BASE_URL =
    (import.meta.env.VITE_API_URL || '/api').replace(/\/+$/, '')

  /** Fetch one page of arguments for a single stance */
  const fetchPage = async (
    stanceFilter: 'pro' | 'con' | undefined,
    offset: number,
    signal: AbortSignal,
  ) => {
    const params: Record<string, unknown> = {
      q: q.value.trim(),
      limit: pageSize,
      offset,
    }
    const claimId = toValue(options.claimId)
    if (claimId) params.claim_id = String(claimId)
    if (stanceFilter) params.stance = stanceFilter
    params.sort = sortField.value === 'quality' ? 'quality_score' : 'relevance_score'

    const { data } = await axios.get(`${API_BASE_URL}/argument-search`, {
      params,
      signal,
    })

    const parsed = SearchResponseSchema.safeParse(data)
    if (!parsed.success) {
      console.error('Invalid search response', parsed.error)
      throw {
        message: 'Invalid server response',
        code: 'INVALID_RESPONSE',
        reason: parsed.error.message,
      }
    }
    return parsed.data
  }

  const runSearch = async (append = false) => {
    err.value = null
    if (!append) {
      resp.value = null
      proResp.value = null
      conResp.value = null
    }
    loading.value = true

    if (currentAbort) currentAbort.abort()
    currentAbort = new AbortController()
    const mySeq = ++seq

    try {
      if (stance.value === 'all') {
        // Fire pro + con in parallel, each gets pageSize results
        const proOffset = append ? (proResp.value?.hits.length ?? 0) : 0
        const conOffset = append ? (conResp.value?.hits.length ?? 0) : 0

        const [proData, conData] = await Promise.all([
          fetchPage('pro', proOffset, currentAbort.signal),
          fetchPage('con', conOffset, currentAbort.signal),
        ])

        if (mySeq === seq) {
          if (append) {
            proResp.value = {
              ...proData,
              hits: [...(proResp.value?.hits ?? []), ...proData.hits],
              total: proData.total ?? proResp.value?.total,
            }
            conResp.value = {
              ...conData,
              hits: [...(conResp.value?.hits ?? []), ...conData.hits],
              total: conData.total ?? conResp.value?.total,
            }
          } else {
            proResp.value = proData
            conResp.value = conData
          }
          // Merge into resp for retrievedArguments / total
          resp.value = {
            hits: [...(proResp.value?.hits ?? []), ...(conResp.value?.hits ?? [])],
            total: (proResp.value?.total ?? 0) + (conResp.value?.total ?? 0),
            took_ms: Math.max(proData.took_ms ?? 0, conData.took_ms ?? 0),
          }
        }
      } else {
        // Single stance — same as before
        const offset = append ? (resp.value?.hits.length ?? 0) : 0
        const data = await fetchPage(stance.value, offset, currentAbort.signal)

        if (mySeq === seq) {
          if (append && resp.value) {
            resp.value = {
              ...data,
              hits: [...resp.value.hits, ...data.hits],
              total: data.total ?? resp.value.total,
            }
          } else {
            resp.value = data
          }
        }
      }
    } catch (e: any) {
      if (axios.isCancel(e)) return

      if (e?.code === 'INVALID_RESPONSE') {
        if (mySeq === seq) {
          err.value = { message: e.message, code: e.code, reason: e.reason }
        }
        return
      }

      const axErr = e as AxiosError<any>
      if (mySeq === seq) {
        err.value = {
          message:
            axErr.response?.data?.message ?? axErr.message ?? 'Request failed',
          code:
            axErr.response?.status ?? axErr.response?.data?.code ?? 'UNKNOWN',
          reason: axErr.response?.data?.reason ?? null,
        }
      }
    } finally {
      if (mySeq === seq) loading.value = false
    }
  }

  /**
   * Fetch one extra argument for a specific stance (used for visual balancing).
   * Returns the new hit if successfully fetched, or null.
   */
  const loadExtraForStance = async (side: 'pro' | 'con'): Promise<ArgumentSearchHit | null> => {
    const sideResp = side === 'pro' ? proResp : conResp
    const current = sideResp.value
    if (!current) return null

    const total = current.total ?? 0
    if (current.hits.length >= total) return null

    try {
      const data = await fetchPage(side, current.hits.length, currentAbort?.signal ?? new AbortController().signal)
      if (data.hits.length === 0) return null

      const extra = data.hits[0]!
      // Append to side response
      sideResp.value = {
        ...current,
        hits: [...current.hits, extra],
        total: data.total ?? current.total,
      }
      // Update merged resp
      resp.value = {
        hits: [...(proResp.value?.hits ?? []), ...(conResp.value?.hits ?? [])],
        total: (proResp.value?.total ?? 0) + (conResp.value?.total ?? 0),
        took_ms: resp.value?.took_ms,
      }
      return extra
    } catch {
      return null
    }
  }

  /* ──────────────────────────────
   * Computed helpers
   * ────────────────────────────── */

  const retrievedArguments = computed<ArgumentSearchHit[]>(() =>
    resp.value?.hits ?? []
  )

  const groupedArguments = computed(() => ({
    pro: proResp.value?.hits ?? retrievedArguments.value.filter(h => h.stance === 'pro'),
    con: conResp.value?.hits ?? retrievedArguments.value.filter(h => h.stance === 'con'),
  }))

  const hasMore = computed(() => {
    if (stance.value === 'all') {
      const proHasMore = (proResp.value?.total ?? 0) > (proResp.value?.hits.length ?? 0)
      const conHasMore = (conResp.value?.total ?? 0) > (conResp.value?.hits.length ?? 0)
      return proHasMore || conHasMore
    }
    const total = resp.value?.total ?? 0
    return total > 0 && retrievedArguments.value.length < total
  })

  const loadMore = async () => {
    if (!hasMore.value || loading.value) return
    await runSearch(true)
  }

  const hasMoreForStance = (side: 'pro' | 'con') => {
    const r = side === 'pro' ? proResp.value : conResp.value
    return (r?.total ?? 0) > (r?.hits.length ?? 0)
  }

  const loadMoreForStance = async (side: 'pro' | 'con') => {
    if (loading.value) return
    if (!hasMoreForStance(side)) return
    const sideResp = side === 'pro' ? proResp : conResp
    loading.value = true
    if (currentAbort) currentAbort.abort()
    currentAbort = new AbortController()
    const mySeq = ++seq
    try {
      const offset = sideResp.value?.hits.length ?? 0
      const data = await fetchPage(side, offset, currentAbort.signal)
      if (mySeq !== seq) return
      const current = sideResp.value
      sideResp.value = {
        ...data,
        hits: [...(current?.hits ?? []), ...data.hits],
        total: data.total ?? current?.total,
      }
      resp.value = {
        hits: [...(proResp.value?.hits ?? []), ...(conResp.value?.hits ?? [])],
        total: (proResp.value?.total ?? 0) + (conResp.value?.total ?? 0),
        took_ms: resp.value?.took_ms,
      }
    } catch (e: any) {
      if (axios.isCancel(e)) return
    } finally {
      if (mySeq === seq) loading.value = false
    }
  }

  const doSearch = async () => {
    await runSearch(false)
  }

  return {
    // state
    q,
    stance,
    sortField,
    resp,
    proResp,
    conResp,
    err,
    loading,

    // results
    retrievedArguments,
    groupedArguments,
    hasMore,

    // actions
    doSearch,
    loadMore,
    loadMoreForStance,
    hasMoreForStance,
    loadExtraForStance,
  }
}
